import {
  CheckCircleOutlined,
  FireOutlined,
  HourglassOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { GET_DASHBOARD_METRIC_STATISTICS } from '@frontend/data-access';
import { Card, Skeleton, Tooltip } from 'antd';
import { useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';

interface TotalStatisticsProps {
  channelId?: string;
  projectId?: string;
  challengeId?: string;
  topicId?: string;
  businessArea?: string;
  countryId?: string;
  metricId?: string;
}

function TotalStatistics({
  metricId,
  channelId,
  projectId,
  challengeId,
  topicId,
  businessArea,
  countryId,
}: TotalStatisticsProps) {
  const { t } = useTranslation('common');

  const [getData, { data, loading }] = useLazyQuery<{
    dashboardMetricStatistic: {
      total: number;
      noMetric: number;
      withMetric: number;
      noResult: number;
      withResult: number;
    };
  }>(GET_DASHBOARD_METRIC_STATISTICS);

  useEffect(() => {
    getData({
      variables: {
        channelId,
        projectId,
        challengeId,
        topicId,
        businessArea,
        countryId,
        metricId,
      },
    });
  }, [
    businessArea,
    challengeId,
    channelId,
    countryId,
    getData,
    metricId,
    projectId,
    topicId,
  ]);

  if (loading) {
    return (
      <Card className="!rounded-2xl">
        <Skeleton active />
      </Card>
    );
  }

  return (
    <Card className="!rounded-2xl">
      <div className="flex flex-row flex-wrap gap-6">
        <div className="flex-1">
          <h3 className="text-primary-600 uppercase font-medium mb-4 flex flex-row items-center gap-2">
            <FireOutlined />
            {t('total_ideas')}
          </h3>
          <div className="text-2xl font-semibold">
            {data?.dashboardMetricStatistic.total}
          </div>
        </div>
        <div className="border-l border-gray-300"></div>
        <div className="flex-1">
          <h3 className="text-primary-600 uppercase font-medium mb-4 flex flex-row items-center gap-2">
            <HourglassOutlined />
            {t('in_progress_ideas')}
          </h3>
          <div className="grid md:grid-cols-2 grid-cols-1">
            <div>
              <div className="text-sm text-gray-900">
                {t('no_metrics')}{' '}
                <Tooltip title={t('no_metrics_tooltip')}>
                  <QuestionCircleOutlined className="cursor-pointer" />
                </Tooltip>
              </div>
              <div className="text-2xl font-semibold">
                {data?.dashboardMetricStatistic.noMetric}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-900">
                {t('with_target_metrics')}{' '}
                <Tooltip title={t('with_target_metrics_tooltip')}>
                  <QuestionCircleOutlined className="cursor-pointer" />
                </Tooltip>
              </div>
              <div className="text-2xl font-semibold">
                {data?.dashboardMetricStatistic.withMetric}
              </div>
            </div>
          </div>
        </div>
        <div className="border-l border-gray-300"></div>
        <div className="flex-1">
          <h3 className="text-primary-600 uppercase font-medium mb-4 flex flex-row items-center gap-2">
            <CheckCircleOutlined />
            {t('implemented_ideas')}
          </h3>
          <div className="grid md:grid-cols-2 grid-cols-1">
            <div>
              <div className="text-sm text-gray-900">
                {t('without_actual_metrics')}{' '}
                <Tooltip title={t('without_actual_metrics_tooltip')}>
                  <QuestionCircleOutlined className="cursor-pointer" />
                </Tooltip>
              </div>
              <div className="text-2xl font-semibold">
                {data?.dashboardMetricStatistic.noResult}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-900">
                {t('with_actual_metrics')}{' '}
                <Tooltip title={t('with_actual_metrics_tooltip')}>
                  <QuestionCircleOutlined className="cursor-pointer" />
                </Tooltip>
              </div>
              <div className="text-2xl font-semibold">
                {data?.dashboardMetricStatistic.withResult}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default TotalStatistics;
